/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import ExcelJS from 'exceljs';
import { DC3Record, CostCenter } from '../types.ts';
import { CORPORATE_LOGO_BASE64 } from './corporateLogoBase64.ts';

export interface DC3ExcelExportOptions {
  fileName?: string;
  reportTitle?: string;
  filterDescription?: string;
  costCenterName?: string;
  generatedBy?: string;
  includeExecutiveSummarySheet?: boolean;
  includeExpiringOnlySheet?: boolean;
  highlightExpiringDays?: number; // default 30
}

/**
 * Calculates real-time expiration metadata for a DC3 record
 */
export function getDC3ValidityInfo(issueDateStr: string, customExpiry?: string): {
  expiryDate: string;
  daysRemaining: number;
  computedStatus: 'vigente' | 'por_renovar' | 'vencido';
  statusLabel: string;
  priorityLabel: string;
} {
  let expiryDateStr = customExpiry;
  if (!expiryDateStr && issueDateStr) {
    const issue = new Date(issueDateStr);
    if (!isNaN(issue.getTime())) {
      const nextYear = new Date(issue);
      nextYear.setFullYear(nextYear.getFullYear() + 1);
      expiryDateStr = nextYear.toISOString().split('T')[0];
    }
  }

  if (!expiryDateStr) {
    const today = new Date();
    today.setFullYear(today.getFullYear() + 1);
    expiryDateStr = today.toISOString().split('T')[0];
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDateStr);
  expiry.setHours(0, 0, 0, 0);

  const diffTime = expiry.getTime() - today.getTime();
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  let computedStatus: 'vigente' | 'por_renovar' | 'vencido' = 'vigente';
  let statusLabel = 'VIGENTE';
  let priorityLabel = 'Cumplimiento Normal';

  if (daysRemaining < 0) {
    computedStatus = 'vencido';
    statusLabel = 'VENCIDO (+1 AÑO)';
    priorityLabel = 'URGENTE: RENOVAR INMEDIATAMENTE';
  } else if (daysRemaining <= 30) {
    computedStatus = 'por_renovar';
    statusLabel = `POR VENCER (${daysRemaining} DÍAS)`;
    priorityLabel = 'ALERTA: PROGRAMAR RENOVACIÓN (30 DÍAS)';
  } else {
    computedStatus = 'vigente';
    statusLabel = `VIGENTE (${daysRemaining} DÍAS)`;
    priorityLabel = 'En Vigencia';
  }

  return {
    expiryDate: expiryDateStr,
    daysRemaining,
    computedStatus,
    statusLabel,
    priorityLabel
  };
}

/**
 * Generates and triggers download of a highly professional STPS DC-3 Excel Workbook
 * with embedded corporate logo, KPI summary cards, customized styling, conditional alerts,
 * and multi-sheet breakdown for upcoming 30-day renewals.
 */
export async function exportDC3RecordsToExcel(
  records: DC3Record[],
  options: DC3ExcelExportOptions = {}
): Promise<void> {
  const {
    fileName = `Reporte_Formatos_DC3_STPS_${new Date().toISOString().split('T')[0]}.xlsx`,
    reportTitle = 'MATRIZ DE CONTROL Y MONITOREO DE FORMATOS DC-3 (STPS)',
    filterDescription = 'Todos los registros evaluados',
    costCenterName = 'Todas las Plantas / Sedes',
    generatedBy = 'Coordinación HSEQ / Seguridad Industrial',
    includeExecutiveSummarySheet = true,
    includeExpiringOnlySheet = true,
    highlightExpiringDays = 30
  } = options;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'SNF - SIC | Sistema Integral de Gestión HSEQ';
  workbook.lastModifiedBy = generatedBy;
  workbook.created = new Date();
  workbook.modified = new Date();

  // Clean base64 logo string
  let logoBase64 = CORPORATE_LOGO_BASE64;
  if (logoBase64.includes('base64,')) {
    logoBase64 = logoBase64.split('base64,')[1];
  }

  let logoImageId: number | null = null;
  try {
    logoImageId = workbook.addImage({
      base64: logoBase64,
      extension: 'jpeg',
    });
  } catch (err) {
    console.warn('[DC3 Excel Export] Error registering corporate logo:', err);
  }

  // Pre-calculate statistics
  const enrichedList = records.map(r => ({
    ...r,
    validity: getDC3ValidityInfo(r.issueDate, r.expiryDate)
  }));

  const totalCount = enrichedList.length;
  const expiringSoonCount = enrichedList.filter(r => r.validity.computedStatus === 'por_renovar').length;
  const expiredCount = enrichedList.filter(r => r.validity.computedStatus === 'vencido').length;
  const validCount = enrichedList.filter(r => r.validity.computedStatus === 'vigente').length;
  const totalTrainingHours = enrichedList.reduce((acc, r) => acc + (Number(r.durationHours) || 0), 0);
  const complianceRate = totalCount > 0 ? Math.round((validCount / totalCount) * 100) : 100;

  // =========================================================================
  // HOJA 1: MATRIZ DE FORMATOS DC-3
  // =========================================================================
  const wsMain = workbook.addWorksheet('Formatos DC-3 STPS', {
    views: [{ showGridLines: true }],
    pageSetup: {
      paperSize: 9, // A4
      orientation: 'landscape',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: {
        left: 0.4,
        right: 0.4,
        top: 0.5,
        bottom: 0.5,
        header: 0.3,
        footer: 0.3
      }
    }
  });

  // Column definitions with optimal widths
  wsMain.columns = [
    { key: 'index', width: 6 },           // A: No.
    { key: 'folio', width: 14 },          // B: Folio
    { key: 'employeeName', width: 28 },   // C: Nombre Colaborador
    { key: 'curp', width: 22 },           // D: CURP
    { key: 'employeeId', width: 14 },     // E: ID / Nómina
    { key: 'role', width: 22 },           // F: Puesto
    { key: 'courseName', width: 36 },     // G: Nombre del Curso / Norma STPS
    { key: 'thematicArea', width: 24 },   // H: Área Temática
    { key: 'durationHours', width: 12 },  // I: Horas
    { key: 'issueDate', width: 14 },      // J: Fecha Emisión (Vigencia Inicial)
    { key: 'expiryDate', width: 14 },     // K: Fecha Vencimiento (1 Año)
    { key: 'daysRemaining', width: 14 },  // L: Días Restantes
    { key: 'status', width: 22 },         // M: Estatus Renovación
    { key: 'priority', width: 26 },       // N: Prioridad / Acción
    { key: 'trainer', width: 26 },        // O: Agente Capacitador
    { key: 'stpsReg', width: 20 },        // P: Registro STPS
    { key: 'costCenter', width: 18 },     // Q: Centro de Costos
    { key: 'score', width: 12 }           // R: Calificación
  ];

  // Insert Logo in A1:B3
  if (logoImageId !== null) {
    try {
      wsMain.addImage(logoImageId, {
        tl: { col: 0.1, row: 0.2 } as any,
        br: { col: 1.9, row: 3.8 } as any,
        editAs: 'oneCell'
      });
    } catch (e) {
      console.warn('Could not place logo in main sheet', e);
    }
  }

  // Row 1: Main Corporate Header Banner
  wsMain.mergeCells('C1:R1');
  const titleCell = wsMain.getCell('C1');
  titleCell.value = 'SNF - SIC | SISTEMA INTEGRAL DE CONTROL Y GESTIÓN HSEQ';
  titleCell.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
  titleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF0F172A' } // Dark Slate Navy
  };
  titleCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  wsMain.getRow(1).height = 28;

  // Row 2: Subtitle
  wsMain.mergeCells('C2:R2');
  const subTitleCell = wsMain.getCell('C2');
  subTitleCell.value = reportTitle.toUpperCase();
  subTitleCell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FF1E293B' } };
  subTitleCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  wsMain.getRow(2).height = 20;

  // Row 3: Scope and Alert Subtitle
  wsMain.mergeCells('C3:R3');
  const alertSubTitle = wsMain.getCell('C3');
  alertSubTitle.value = `PROGRAMA DE RENOVACIÓN OPORTUNA DE CONSTANCIAS (VENCIMIENTOS A ${highlightExpiringDays} DÍAS) - SECRETARÍA DEL TRABAJO Y PREVISIÓN SOCIAL (STPS)`;
  alertSubTitle.font = { name: 'Calibri', size: 9.5, italic: true, color: { argb: 'FF64748B' } };
  alertSubTitle.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  wsMain.getRow(3).height = 18;

  // Row 4: Metadata bar
  wsMain.getRow(4).height = 22;
  const metaLabels = [
    { cell: 'A4', val: 'EMISIÓN:', bold: true },
    { cell: 'B4', val: new Date().toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }), bold: false },
    { cell: 'C4', val: 'CENTRO:', bold: true },
    { cell: 'D4', val: costCenterName, bold: false },
    { cell: 'G4', val: 'FILTRO APLICADO:', bold: true },
    { cell: 'H4', val: filterDescription, bold: false },
    { cell: 'L4', val: 'AUDITOR:', bold: true },
    { cell: 'M4', val: generatedBy, bold: false }
  ];

  metaLabels.forEach(m => {
    const c = wsMain.getCell(m.cell);
    c.value = m.val;
    c.font = { name: 'Calibri', size: 9, bold: m.bold, color: { argb: m.bold ? 'FF0F172A' : 'FF334155' } };
    c.alignment = { vertical: 'middle' };
  });

  // Empty separator
  wsMain.getRow(5).height = 8;

  // Rows 6-7: Executive KPI summary blocks
  wsMain.getRow(6).height = 18;
  wsMain.getRow(7).height = 24;

  // KPI 1: Total
  wsMain.mergeCells('A6:C6');
  wsMain.getCell('A6').value = 'TOTAL FORMATOS DC-3';
  wsMain.getCell('A6').font = { name: 'Calibri', size: 8.5, bold: true, color: { argb: 'FF475569' } };
  wsMain.getCell('A6').alignment = { horizontal: 'center', vertical: 'middle' };
  wsMain.getCell('A6').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };

  wsMain.mergeCells('A7:C7');
  wsMain.getCell('A7').value = `${totalCount} Registros`;
  wsMain.getCell('A7').font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FF0F172A' } };
  wsMain.getCell('A7').alignment = { horizontal: 'center', vertical: 'middle' };
  wsMain.getCell('A7').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };

  // KPI 2: Vigentes
  wsMain.mergeCells('D6:F6');
  wsMain.getCell('D6').value = 'VIGENTES (> 30 DÍAS)';
  wsMain.getCell('D6').font = { name: 'Calibri', size: 8.5, bold: true, color: { argb: 'FF047857' } };
  wsMain.getCell('D6').alignment = { horizontal: 'center', vertical: 'middle' };
  wsMain.getCell('D6').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } };

  wsMain.mergeCells('D7:F7');
  wsMain.getCell('D7').value = `${validCount} Constancias`;
  wsMain.getCell('D7').font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FF065F46' } };
  wsMain.getCell('D7').alignment = { horizontal: 'center', vertical: 'middle' };
  wsMain.getCell('D7').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFECFDF5' } };

  // KPI 3: Próximos a Vencer (<= 30 Días) - Special Alert Highlight!
  wsMain.mergeCells('G6:J6');
  wsMain.getCell('G6').value = `⚠️ PRÓXIMOS A VENCER (≤ ${highlightExpiringDays} DÍAS) - RENOVAR`;
  wsMain.getCell('G6').font = { name: 'Calibri', size: 8.5, bold: true, color: { argb: 'FF9A3412' } };
  wsMain.getCell('G6').alignment = { horizontal: 'center', vertical: 'middle' };
  wsMain.getCell('G6').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };

  wsMain.mergeCells('G7:J7');
  wsMain.getCell('G7').value = `${expiringSoonCount} Por Renovar`;
  wsMain.getCell('G7').font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFB45309' } };
  wsMain.getCell('G7').alignment = { horizontal: 'center', vertical: 'middle' };
  wsMain.getCell('G7').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFBEB' } };

  // KPI 4: Vencidos
  wsMain.mergeCells('K6:M6');
  wsMain.getCell('K6').value = '🔴 VENCIDOS (+1 AÑO)';
  wsMain.getCell('K6').font = { name: 'Calibri', size: 8.5, bold: true, color: { argb: 'FF991B1B' } };
  wsMain.getCell('K6').alignment = { horizontal: 'center', vertical: 'middle' };
  wsMain.getCell('K6').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } };

  wsMain.mergeCells('K7:M7');
  wsMain.getCell('K7').value = `${expiredCount} Vencidos`;
  wsMain.getCell('K7').font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFB91C1C' } };
  wsMain.getCell('K7').alignment = { horizontal: 'center', vertical: 'middle' };
  wsMain.getCell('K7').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF2F2' } };

  // KPI 5: Cumplimiento Normativo & Horas
  wsMain.mergeCells('N6:R6');
  wsMain.getCell('N6').value = 'CUMPLIMIENTO NORMATIVO & CAPACITACIÓN';
  wsMain.getCell('N6').font = { name: 'Calibri', size: 8.5, bold: true, color: { argb: 'FF1E40AF' } };
  wsMain.getCell('N6').alignment = { horizontal: 'center', vertical: 'middle' };
  wsMain.getCell('N6').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } };

  wsMain.mergeCells('N7:R7');
  wsMain.getCell('N7').value = `${complianceRate}% Vigente (${totalTrainingHours} Horas Totales)`;
  wsMain.getCell('N7').font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF1E3A8A' } };
  wsMain.getCell('N7').alignment = { horizontal: 'center', vertical: 'middle' };
  wsMain.getCell('N7').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFF6FF' } };

  // Apply borders to KPI boxes
  ['A6', 'A7', 'D6', 'D7', 'G6', 'G7', 'K6', 'K7', 'N6', 'N7'].forEach(cellRef => {
    const cell = wsMain.getCell(cellRef);
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
    };
  });

  // Empty separator before Table Header
  wsMain.getRow(8).height = 10;

  // Row 9: Table Column Headers
  const headerRow = wsMain.getRow(9);
  headerRow.height = 30;

  const headerTitles = [
    'No.',
    'Folio DC-3',
    'Nombre del Colaborador',
    'CURP',
    'No. Nómina / ID',
    'Puesto / Ocupación',
    'Nombre del Curso / Norma STPS',
    'Área Temática STPS',
    'Horas',
    'Fecha Emisión',
    'Fecha Vencimiento',
    'Días Restantes',
    'Estatus',
    'Prioridad de Renovación',
    'Agente Capacitador / Instructor',
    'Registro STPS',
    'Centro de Costos',
    'Calificación'
  ];

  headerRow.values = headerTitles;

  headerRow.eachCell((cell) => {
    cell.font = { name: 'Calibri', size: 9.5, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1E293B' } // Navy Header
    };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'medium', color: { argb: 'FF0F172A' } },
      left: { style: 'thin', color: { argb: 'FF334155' } },
      bottom: { style: 'medium', color: { argb: 'FF0F172A' } },
      right: { style: 'thin', color: { argb: 'FF334155' } }
    };
  });

  // Populate Data Rows
  let currentRowNum = 10;

  enrichedList.forEach((rec, idx) => {
    const row = wsMain.getRow(currentRowNum);
    row.height = 22;

    const isExpiring = rec.validity.computedStatus === 'por_renovar';
    const isExpired = rec.validity.computedStatus === 'vencido';

    row.values = [
      idx + 1,
      rec.id || `DC3-${idx + 1}`,
      rec.employeeName || 'N/A',
      rec.curp || 'N/A',
      rec.employeeId || 'N/A',
      rec.role || 'Colaborador',
      rec.courseName || 'N/A',
      rec.thematicArea || '6000 - Seguridad',
      Number(rec.durationHours) || 16,
      rec.issueDate || 'N/A',
      rec.validity.expiryDate || 'N/A',
      rec.validity.daysRemaining,
      rec.validity.statusLabel,
      rec.validity.priorityLabel,
      rec.trainer || 'Instructor HSEQ',
      rec.stpsReg || 'N/A',
      rec.costCenterName || costCenterName,
      rec.score ? String(rec.score) : '100%'
    ];

    // Determine row background styling (Zebra vs Alert Highlights)
    let rowBgArgb = idx % 2 === 0 ? 'FFFFFFFF' : 'FFF8FAFC';
    if (isExpired) {
      rowBgArgb = 'FFFFF1F2'; // Soft light red
    } else if (isExpiring) {
      rowBgArgb = 'FFFFFBEB'; // Soft warm yellow/amber
    }

    row.eachCell((cell, colNumber) => {
      cell.font = { name: 'Calibri', size: 9, color: { argb: 'FF1E293B' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: rowBgArgb }
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
        left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
        bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
        right: { style: 'thin', color: { argb: 'FFE2E8F0' } }
      };

      // Alignment adjustments
      if ([1, 2, 4, 5, 9, 10, 11, 12, 16, 18].includes(colNumber)) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      } else {
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
      }

      // Folio highlight
      if (colNumber === 2) {
        cell.font = { name: 'Consolas', size: 9, bold: true, color: { argb: 'FFBE123C' } };
      }

      // Course Name and Collaborator bold
      if (colNumber === 3 || colNumber === 7) {
        cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF0F172A' } };
      }

      // Days Remaining column badge
      if (colNumber === 12) {
        if (isExpired) {
          cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FFB91C1C' } };
        } else if (isExpiring) {
          cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FFB45309' } };
        } else {
          cell.font = { name: 'Calibri', size: 9, color: { argb: 'FF047857' } };
        }
      }

      // Status Column Badge
      if (colNumber === 13) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        if (isExpired) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } };
          cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF991B1B' } };
        } else if (isExpiring) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
          cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF92400E' } };
        } else {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } };
          cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF065F46' } };
        }
      }

      // Priority Action Column
      if (colNumber === 14) {
        if (isExpired) {
          cell.font = { name: 'Calibri', size: 8.5, bold: true, color: { argb: 'FFDC2626' } };
        } else if (isExpiring) {
          cell.font = { name: 'Calibri', size: 8.5, bold: true, color: { argb: 'FFD97706' } };
        } else {
          cell.font = { name: 'Calibri', size: 8.5, color: { argb: 'FF64748B' } };
        }
      }
    });

    currentRowNum++;
  });

  // Table Auto-Filter
  wsMain.autoFilter = {
    from: { row: 9, column: 1 },
    to: { row: currentRowNum - 1, column: 18 }
  };

  // Bottom Space before Signatures
  currentRowNum += 2;

  // Signatures Header Box
  wsMain.mergeCells(`A${currentRowNum}:R${currentRowNum}`);
  const sigTitle = wsMain.getCell(`A${currentRowNum}`);
  sigTitle.value = 'VALIDACIÓN NORMATIVA Y CONTROL DE LA COMISIÓN MIXTA DE CAPACITACIÓN, ADIESTRAMIENTO Y PRODUCTIVIDAD';
  sigTitle.font = { name: 'Calibri', size: 9.5, bold: true, color: { argb: 'FF0F172A' } };
  sigTitle.alignment = { horizontal: 'center', vertical: 'middle' };
  sigTitle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
  wsMain.getRow(currentRowNum).height = 20;

  currentRowNum += 2;

  // 3 Signature boxes
  const sigLineRow = currentRowNum;
  const sigNameRow = currentRowNum + 1;
  const sigRoleRow = currentRowNum + 2;

  // Box 1: Representante del Patrón
  wsMain.mergeCells(`B${sigLineRow}:E${sigLineRow}`);
  wsMain.getCell(`B${sigLineRow}`).border = { bottom: { style: 'thin', color: { argb: 'FF0F172A' } } };
  wsMain.mergeCells(`B${sigNameRow}:E${sigNameRow}`);
  wsMain.getCell(`B${sigNameRow}`).value = 'REPRESENTANTE DE LA EMPRESA / PATRÓN';
  wsMain.getCell(`B${sigNameRow}`).font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF0F172A' } };
  wsMain.getCell(`B${sigNameRow}`).alignment = { horizontal: 'center' };
  wsMain.mergeCells(`B${sigRoleRow}:E${sigRoleRow}`);
  wsMain.getCell(`B${sigRoleRow}`).value = 'Firma y Sello de Autorización STPS';
  wsMain.getCell(`B${sigRoleRow}`).font = { name: 'Calibri', size: 8, italic: true, color: { argb: 'FF64748B' } };
  wsMain.getCell(`B${sigRoleRow}`).alignment = { horizontal: 'center' };

  // Box 2: Representante de los Trabajadores
  wsMain.mergeCells(`H${sigLineRow}:K${sigLineRow}`);
  wsMain.getCell(`H${sigLineRow}`).border = { bottom: { style: 'thin', color: { argb: 'FF0F172A' } } };
  wsMain.mergeCells(`H${sigNameRow}:K${sigNameRow}`);
  wsMain.getCell(`H${sigNameRow}`).value = 'REPRESENTANTE DE LOS TRABAJADORES';
  wsMain.getCell(`H${sigNameRow}`).font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF0F172A' } };
  wsMain.getCell(`H${sigNameRow}`).alignment = { horizontal: 'center' };
  wsMain.mergeCells(`H${sigRoleRow}:K${sigRoleRow}`);
  wsMain.getCell(`H${sigRoleRow}`).value = 'Comisión Mixta de Capacitación';
  wsMain.getCell(`H${sigRoleRow}`).font = { name: 'Calibri', size: 8, italic: true, color: { argb: 'FF64748B' } };
  wsMain.getCell(`H${sigRoleRow}`).alignment = { horizontal: 'center' };

  // Box 3: Responsable de Seguridad e Higiene HSEQ
  wsMain.mergeCells(`N${sigLineRow}:Q${sigLineRow}`);
  wsMain.getCell(`N${sigLineRow}`).border = { bottom: { style: 'thin', color: { argb: 'FF0F172A' } } };
  wsMain.mergeCells(`N${sigNameRow}:Q${sigNameRow}`);
  wsMain.getCell(`N${sigNameRow}`).value = 'COORDINADOR DE SEGURIDAD & SALUD (HSEQ)';
  wsMain.getCell(`N${sigNameRow}`).font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF0F172A' } };
  wsMain.getCell(`N${sigNameRow}`).alignment = { horizontal: 'center' };
  wsMain.mergeCells(`N${sigRoleRow}:Q${sigRoleRow}`);
  wsMain.getCell(`N${sigRoleRow}`).value = generatedBy;
  wsMain.getCell(`N${sigRoleRow}`).font = { name: 'Calibri', size: 8, italic: true, color: { argb: 'FF64748B' } };
  wsMain.getCell(`N${sigRoleRow}`).alignment = { horizontal: 'center' };


  // =========================================================================
  // HOJA 2: ALERTAS DE RENOVACIÓN (VENCE EN <= 30 DÍAS & VENCIDOS)
  // =========================================================================
  if (includeExpiringOnlySheet) {
    const alertRecords = enrichedList.filter(
      r => r.validity.computedStatus === 'por_renovar' || r.validity.computedStatus === 'vencido'
    );

    const wsAlerts = workbook.addWorksheet(`Alertas Renovación (30 Días)`, {
      views: [{ showGridLines: true }],
      pageSetup: {
        paperSize: 9,
        orientation: 'landscape',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0
      }
    });

    wsAlerts.columns = [
      { key: 'index', width: 6 },
      { key: 'priority', width: 18 },
      { key: 'employeeName', width: 28 },
      { key: 'employeeId', width: 14 },
      { key: 'role', width: 22 },
      { key: 'courseName', width: 36 },
      { key: 'issueDate', width: 14 },
      { key: 'expiryDate', width: 14 },
      { key: 'daysRemaining', width: 16 },
      { key: 'actionRequired', width: 32 },
      { key: 'costCenter', width: 20 },
      { key: 'trainer', width: 24 }
    ];

    // Banner Header in Alert Sheet
    wsAlerts.mergeCells('A1:L1');
    const aTitle = wsAlerts.getCell('A1');
    aTitle.value = `PLAN DE ACCIÓN: CONSTANCIAS DC-3 POR RENOVAR (VENTANA CRÍTICA DE ${highlightExpiringDays} DÍAS)`;
    aTitle.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
    aTitle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD97706' } }; // Amber Header
    aTitle.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    wsAlerts.getRow(1).height = 28;

    wsAlerts.mergeCells('A2:L2');
    const aDesc = wsAlerts.getCell('A2');
    aDesc.value = `Total de Constancias en Alerta: ${alertRecords.length} (${expiringSoonCount} por vencer en ≤ 30 días, ${expiredCount} vencidas). Programa de Re-Capacitación STPS 2026.`;
    aDesc.font = { name: 'Calibri', size: 9.5, italic: true, color: { argb: 'FF78350F' } };
    aDesc.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    wsAlerts.getRow(2).height = 20;

    // Headers in Row 4
    const aHeaders = wsAlerts.getRow(4);
    aHeaders.height = 26;
    aHeaders.values = [
      'No.',
      'Prioridad',
      'Nombre del Colaborador',
      'No. Nómina',
      'Puesto / Ocupación',
      'Nombre del Curso / Norma STPS',
      'Fecha Emisión',
      'Fecha Vencimiento',
      'Días Restantes',
      'Acción Sugerida / Plan de Renovación',
      'Centro de Costos',
      'Capacitador Asignado'
    ];

    aHeaders.eachCell(cell => {
      cell.font = { name: 'Calibri', size: 9.5, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF78350F' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF451A03' } },
        bottom: { style: 'medium', color: { argb: 'FF451A03' } },
        left: { style: 'thin', color: { argb: 'FF451A03' } },
        right: { style: 'thin', color: { argb: 'FF451A03' } }
      };
    });

    let aRowIndex = 5;
    alertRecords
      .sort((a, b) => a.validity.daysRemaining - b.validity.daysRemaining)
      .forEach((rec, idx) => {
        const row = wsAlerts.getRow(aRowIndex);
        row.height = 22;
        const isExp = rec.validity.computedStatus === 'vencido';

        row.values = [
          idx + 1,
          isExp ? '🔴 CRÍTICA / VENCIDO' : '⚠️ URGENTE (≤ 30d)',
          rec.employeeName,
          rec.employeeId || 'N/A',
          rec.role || 'Colaborador',
          rec.courseName,
          rec.issueDate,
          rec.validity.expiryDate,
          rec.validity.daysRemaining < 0 ? `${Math.abs(rec.validity.daysRemaining)} días vencido` : `${rec.validity.daysRemaining} días restantes`,
          isExp ? 'Programar curso STPS de inmediato' : 'Coordinar fecha de curso con RRHH y Capacitador',
          rec.costCenterName || costCenterName,
          rec.trainer
        ];

        row.eachCell((cell, colNum) => {
          cell.font = { name: 'Calibri', size: 9, color: { argb: 'FF1E293B' } };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: isExp ? 'FFFEE2E2' : 'FFFEF3C7' }
          };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
            bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
            left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
            right: { style: 'thin', color: { argb: 'FFE2E8F0' } }
          };

          if ([1, 2, 4, 7, 8, 9].includes(colNum)) {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          } else {
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
          }

          if (colNum === 2) {
            cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: isExp ? 'FF991B1B' : 'FF9A3412' } };
          }
          if (colNum === 3 || colNum === 6) {
            cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF0F172A' } };
          }
        });

        aRowIndex++;
      });
  }

  // =========================================================================
  // HOJA 3: RESUMEN EJECUTIVO POR CURSO & CENTRO DE COSTOS
  // =========================================================================
  if (includeExecutiveSummarySheet) {
    const wsSummary = workbook.addWorksheet('Resumen Normativo STPS', {
      views: [{ showGridLines: true }]
    });

    wsSummary.columns = [
      { key: 'course', width: 40 },
      { key: 'total', width: 14 },
      { key: 'valid', width: 14 },
      { key: 'expiring', width: 18 },
      { key: 'expired', width: 14 },
      { key: 'pct', width: 18 }
    ];

    wsSummary.mergeCells('A1:F1');
    const sTitle = wsSummary.getCell('A1');
    sTitle.value = 'CONSOLIDADO DE CUMPLIMIENTO POR NORMA / CURSO DE CAPACITACIÓN STPS';
    sTitle.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
    sTitle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F172A' } };
    sTitle.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    wsSummary.getRow(1).height = 26;

    // Group by course
    const courseMap = new Map<string, { total: number; valid: number; expiring: number; expired: number }>();
    enrichedList.forEach(r => {
      const cName = r.courseName || 'Sin Especificar';
      if (!courseMap.has(cName)) {
        courseMap.set(cName, { total: 0, valid: 0, expiring: 0, expired: 0 });
      }
      const data = courseMap.get(cName)!;
      data.total++;
      if (r.validity.computedStatus === 'vigente') data.valid++;
      else if (r.validity.computedStatus === 'por_renovar') data.expiring++;
      else if (r.validity.computedStatus === 'vencido') data.expired++;
    });

    const sHeader = wsSummary.getRow(3);
    sHeader.height = 24;
    sHeader.values = [
      'Nombre del Curso / Norma STPS',
      'Total Formatos',
      'Vigentes (> 30d)',
      'Por Vencer (≤ 30d)',
      'Vencidos',
      '% Cumplimiento Vigente'
    ];
    sHeader.eachCell(cell => {
      cell.font = { name: 'Calibri', size: 9.5, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    let sRowIndex = 4;
    courseMap.forEach((stats, cName) => {
      const row = wsSummary.getRow(sRowIndex);
      row.height = 20;
      const rate = stats.total > 0 ? Math.round((stats.valid / stats.total) * 100) : 100;
      row.values = [
        cName,
        stats.total,
        stats.valid,
        stats.expiring,
        stats.expired,
        `${rate}%`
      ];

      row.eachCell((cell, colNum) => {
        cell.font = { name: 'Calibri', size: 9 };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
          bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
          left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
          right: { style: 'thin', color: { argb: 'FFE2E8F0' } }
        };
        if (colNum === 1) {
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
          cell.font = { name: 'Calibri', size: 9, bold: true };
        } else {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        }
      });
      sRowIndex++;
    });
  }

  // =========================================================================
  // WRITE AND TRIGGER DOWNLOAD IN BROWSER
  // =========================================================================
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
